import { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import { getConversation } from "@/api/conversationsApi";
import BotMessageAvatar from "@/components/chat/BotMessageAvatar";
import AgentMessageAvatar from "@/components/chat/AgentMessageAvatar";
import ChatMessageBody from "@/components/chat/ChatMessageBody";
import { isAgentMessageSender, isBotMessageSender } from "@/lib/chatMessageSenders";
import { formatMessageTime } from "@/lib/formatMessageTime";
import { TranscriptDocumentBuilder } from "@/lib/transcript/TranscriptDocumentBuilder";
import { TranscriptTxtExporter } from "@/lib/transcript/TranscriptTxtExporter";
import { TranscriptPdfExporter } from "@/lib/transcript/TranscriptPdfExporter";

/**
 * Read-only modal showing a full prior conversation transcript (live inbox or dev stub).
 */
export default function HistoricThreadModal({
  open,
  sessionId,
  workspaceId,
  centralInbox = false,
  sessionWorkspaceId = null,
  onClose,
  previewLabel = "",
  showTranscriptExports = false,
}) {
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exportBusy, setExportBusy] = useState(null);
  const bottomRef = useRef(null);
  const documentBuilder = useMemo(() => new TranscriptDocumentBuilder(), []);
  const txtExporter = useMemo(() => new TranscriptTxtExporter({ documentBuilder }), [documentBuilder]);
  const pdfExporter = useMemo(() => new TranscriptPdfExporter({ documentBuilder }), [documentBuilder]);

  useEffect(() => {
    if (!open || !sessionId) {
      setConversation(null);
      setError("");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const { data } = await getConversation({
          id: sessionId,
          workspaceId: centralInbox ? null : (workspaceId ?? null),
          centralInbox,
          sessionWorkspaceId: centralInbox ? sessionWorkspaceId : null,
        });
        if (cancelled) return;
        if (!data) {
          setError("This conversation could not be loaded.");
          setConversation(null);
        } else {
          setConversation(data);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load conversation.");
          setConversation(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sessionId, workspaceId, centralInbox, sessionWorkspaceId]);

  useEffect(() => {
    if (open && conversation?.messages?.length) {
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [open, conversation]);

  const title = conversation?.user?.name || "Prior conversation";
  const messages = Array.isArray(conversation?.messages) ? conversation.messages : [];

  const handleExport = async (kind) => {
    if (!conversation) {
      toast.error("Transcript is still loading.");
      return;
    }
    setExportBusy(kind);
    try {
      if (kind === "txt") await txtExporter.export(conversation);
      else await pdfExporter.export(conversation);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not download transcript.");
    } finally {
      setExportBusy(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center p-3 sm:items-center sm:p-6" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="historic-thread-title"
        className="relative z-10 flex h-[min(88vh,720px)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-2xl shadow-slate-900/25 ring-1 ring-slate-900/[0.06] dark:border-slate-700/80 dark:bg-slate-900 dark:shadow-black/40 dark:ring-slate-700/50"
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        tabIndex={-1}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 bg-slate-50/90 px-4 py-3 dark:border-slate-700/70 dark:bg-slate-900/95 sm:px-5">
          <div className="min-w-0">
            <h2 id="historic-thread-title" className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
              {loading ? "Loading…" : title}
            </h2>
            <p className="mt-0.5 truncate text-[11px] text-slate-500 dark:text-slate-400">
              Session <span className="font-mono text-slate-600 dark:text-slate-300">{sessionId}</span>
              {previewLabel ? ` · ${previewLabel}` : ""}
            </p>
            {conversation && (
              <p className="mt-1 text-xs text-slate-600 line-clamp-2 dark:text-slate-300">{conversation.preview || "—"}</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {showTranscriptExports ? (
              <>
                <button
                  type="button"
                  disabled={!conversation || Boolean(exportBusy)}
                  onClick={() => void handleExport("txt")}
                  className="rounded-lg bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-700"
                >
                  {exportBusy === "txt" ? "…" : "Download TXT"}
                </button>
                <button
                  type="button"
                  disabled={!conversation || Boolean(exportBusy)}
                  onClick={() => void handleExport("pdf")}
                  className="rounded-lg bg-teal-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-teal-500 disabled:opacity-50"
                >
                  {exportBusy === "pdf" ? "…" : "Download PDF"}
                </button>
              </>
            ) : null}
            <button type="button" onClick={onClose} className="ui-btn-icon !h-9 !w-9 shrink-0" aria-label="Close">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </header>

        <div className="scrollbar-thin min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-50/30 px-3 py-4 dark:bg-slate-950/45 sm:px-5 sm:py-5">
          {loading && (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm text-slate-500">
              <div className="h-8 w-8 animate-pulse rounded-full bg-slate-200" />
              Loading transcript…
            </div>
          )}
          {!loading && error && (
            <p className="rounded-lg border border-rose-200 bg-rose-50/90 px-3 py-2 text-sm text-rose-800">{error}</p>
          )}
          {!loading && !error && messages.length === 0 && <p className="text-center text-sm text-slate-500">No messages in this thread.</p>}
          {!loading &&
            !error &&
            messages.map((msg) => {
              if (msg.type === "system" || msg.sender === "system") {
                return (
                  <div key={msg.id} className="flex items-center gap-2 py-0.5">
                    <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
                    <div className="max-w-[92%] rounded-full bg-slate-100/90 px-2.5 py-1 text-[11px] font-medium text-slate-500 ring-1 ring-slate-200/60 dark:bg-slate-800/80 dark:text-slate-300 dark:ring-slate-700">
                      {msg.text}
                    </div>
                    <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
                  </div>
                );
              }
              if (msg.sender === "user") {
                return (
                  <div key={msg.id} className="flex min-w-0 items-end gap-2">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                      <i className="ri-user-3-line text-xs" />
                    </div>
                    <div className="chat-message-column min-w-0 max-w-[min(80%,480px)]">
                      <div className="chat-message-bubble rounded-2xl rounded-bl-sm border border-slate-200/80 bg-white px-3.5 py-2.5 text-sm leading-relaxed text-slate-800 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                        <ChatMessageBody text={msg.text} className="dark:[&_.chat-md-p]:text-slate-100 dark:[&_.chat-md-list]:text-slate-100" />
                      </div>
                      {formatMessageTime(msg) && (
                        <p className="mt-0.5 pl-0.5 text-[10px] text-slate-400">{formatMessageTime(msg)}</p>
                      )}
                    </div>
                  </div>
                );
              }
              if (isBotMessageSender(msg.sender)) {
                return (
                  <div key={msg.id} className="flex min-w-0 items-end gap-2">
                    <BotMessageAvatar size="sm" />
                    <div className="chat-message-column min-w-0 max-w-[min(80%,480px)]">
                      <div className="chat-message-bubble rounded-2xl rounded-bl-sm border border-violet-200/80 bg-violet-50/90 px-3.5 py-2.5 text-sm leading-relaxed text-slate-800 shadow-sm dark:border-violet-800/60 dark:bg-violet-950/45 dark:text-slate-100">
                        <ChatMessageBody text={msg.text} className="dark:[&_.chat-md-p]:text-slate-100 dark:[&_.chat-md-list]:text-slate-100 dark:[&_strong]:text-white" />
                      </div>
                      {formatMessageTime(msg) && (
                        <p className="mt-0.5 pl-0.5 text-[10px] text-slate-400">{formatMessageTime(msg)}</p>
                      )}
                    </div>
                  </div>
                );
              }
              if (msg.sender === "note") {
                return (
                  <div key={msg.id} className="flex min-w-0 items-end justify-end gap-2">
                    <div className="chat-message-column min-w-0 max-w-[min(80%,480px)]">
                      <div className="chat-message-bubble rounded-2xl rounded-br-sm border border-amber-200/90 bg-amber-50/95 px-3.5 py-2.5 text-sm text-amber-950 ring-1 ring-amber-100/80 dark:border-amber-800/60 dark:bg-amber-950/35 dark:text-amber-100 dark:ring-amber-900/50">
                        <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-amber-700/90 dark:text-amber-300">Note</span>
                        {msg.text}
                      </div>
                      {formatMessageTime(msg) && (
                        <p className="mt-0.5 pr-0.5 text-right text-[10px] text-slate-400">{formatMessageTime(msg)}</p>
                      )}
                    </div>
                    <AgentMessageAvatar size="sm" variant="note" />
                  </div>
                );
              }
              if (isAgentMessageSender(msg.sender)) {
                return (
                  <div key={msg.id} className="flex min-w-0 items-end justify-end gap-2">
                    <div className="chat-message-column min-w-0 max-w-[min(80%,480px)]">
                      <div className="chat-message-bubble rounded-2xl rounded-br-sm bg-teal-700 px-3.5 py-2.5 text-sm text-white shadow-sm [&_.chat-md-p]:text-white [&_.chat-md-list]:text-white [&_strong]:text-white [&_a.chat-md-link]:text-blue-300 [&_a.chat-md-link]:underline [&_a.chat-md-link]:underline-offset-2 [&_a.chat-md-link:hover]:text-blue-200">
                        <ChatMessageBody text={msg.text} />
                      </div>
                      {formatMessageTime(msg) && (
                        <p className="mt-0.5 pr-0.5 text-right text-[10px] text-slate-400">{formatMessageTime(msg)}</p>
                      )}
                    </div>
                    <AgentMessageAvatar size="sm" />
                  </div>
                );
              }
              return null;
            })}
          <div ref={bottomRef} />
        </div>

        <footer className="shrink-0 border-t border-slate-200/80 bg-white px-4 py-2.5 text-center text-[11px] text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 sm:px-5">
          Read-only transcript. Reply in the main thread after selecting that conversation in the inbox if needed.
        </footer>
      </div>
    </div>
  );
}
